import { validatePresence } from 'ember-changeset-validations/validators';
import { getValidatedIntent } from 'volta/models/simulation';
import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api';

import { attr, belongsTo, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

import BaseModel from './base-model';

import type Simulation from 'volta/models/simulation';
import type SimulationItemSetting from 'volta/models/simulation-item-setting';
import type {
  ICreateScenarioCmd,
  IDuplicateScenarioCmd,
  IUpdateScenarioCmd,
  TScenarioReport,
  TScenarioStatus
} from 'volta/models/types/simulation';
import type { ICalculatedPlanning, ISimulateBufferCmd } from './types/planning';
import type { ICmdResult } from 'volta/utils/api/jsonapi-types';

export const Validations = {
  name: [validatePresence(true)]
};

export const ScenarioResourceName = {
  singular: 'simulationScenario',
  plural: 'simulationScenarios'
};

export const Commands = {
  RunScenario: 'RunScenario',
  DuplicateScenario: 'DuplicateScenario',
  CreateScenario: 'CreateScenario',
  UpdateScenario: 'UpdateScenario',
  SimulateBuffer: 'SimulateBuffer'
};

export const SimulationScenarioEvents = {
  SimulationScenarioCalculationDone: 'SimulationScenarioCalculationDone',
  SimulationScenarioCalculationFailed: 'SimulationScenarioCalculationFailed',
  SimulationScenarioProgress: 'SimulationScenarioProgress',
  SimulationScenarioCalculationStarted: 'SimulationScenarioCalculationStarted'
};

export default class SimulationScenario extends BaseModel {
  // Collection Commands
  // ~~~~
  static createScenario = collectionCommand<
    ICreateScenarioCmd,
    SimulationScenario,
    typeof SimulationScenario
  >(Commands.CreateScenario);

  static duplicateScenario = collectionCommand<
    IDuplicateScenarioCmd,
    SimulationScenario,
    typeof SimulationScenario
  >(Commands.DuplicateScenario);

  static simulateBuffer = collectionCommand<
    ISimulateBufferCmd,
    ICmdResult<ICalculatedPlanning>,
    typeof SimulationScenario
  >(Commands.SimulateBuffer);

  // Resource Commands
  // ~~~~

  runScenario = command<undefined, SimulationScenario>(Commands.RunScenario);
  updateScenario = command<IUpdateScenarioCmd, SimulationScenario>(Commands.UpdateScenario);

  // Properties
  // ~~~~

  @attr('string') simulationId!: string;
  @attr('string') name!: string;
  @attr('string') description?: string;
  @attr('boolean') isRef!: boolean;
  @attr('string', { defaultValue: 'READY' }) status?: TScenarioStatus;
  @attr('boolean', { defaultValue: false }) useExplosion!: boolean;
  @attr('number') settingsValidated?: number;
  @attr('number') forecastValidated?: number;

  @belongsTo('simulation', { async: false, inverse: null }) simulation!: Simulation;
  @hasMany('simulation-item-setting', { async: false, inverse: 'scenario' })
  itemSettings?: SimulationItemSetting[];
  @attr() stats?: TScenarioReport;

  // Getters & CPs
  // ~~~~

  get isScenarioLoading() {
    return this.isWaiting || this.isCalculating;
  }

  get cannotRun() {
    return this.isScenarioLoading || this.isDone || this.isRef;
  }

  get isCalculating() {
    return this.status === 'CALCULATING';
  }

  get isWaiting() {
    return this.status === 'WAITING';
  }

  get isReady() {
    return this.status === 'READY';
  }

  get isDone() {
    return this.status === 'DONE';
  }

  get isCalculated() {
    return this.isDone && !this.isRef;
  }

  get validatedPercent() {
    return ((this.settingsValidated ?? 0) + (this.forecastValidated ?? 0)) / 2;
  }

  get validatedIntent() {
    const pct = this.validatedPercent;
    return getValidatedIntent(pct ?? 0);
  }

  [Type] = 'simulation-scenario' as const;
}
